import React, { useCallback, useMemo } from "react";

// Components
import { Link } from "react-router-dom";
import { IonHeader, useIonRouter } from "@ionic/react";
import { Button, Icon, Image, UserPicture } from "@france/superelements/lib";

// Navigation
import ROUTES from "../../router/constants/routes";

// Style & Configs
import "./MainHeader.scss";
import {
  MSMMediaQueries,
  useLocalize,
  useLocalizeConfig,
} from "@france/superelements/utils";
import {
  UserFragment,
  useGetMeQuery,
  useGetUnreadNotificationsCountQuery,
} from "../../gql/hooks";

interface MainHeaderProps {
  displayName?: boolean;
  logoMediaQuery?: MSMMediaQueries | "all";
}

const MainHeader: React.FC<MainHeaderProps> = ({ displayName }) => {
  // Hook
  const t = useLocalize();
  const { push } = useIonRouter();
  const { data: user } = useGetMeQuery();
  const { currentLang, setLanguage, availableLangs } = useLocalizeConfig();

  // Memos
  const langValues = useMemo(
    () =>
      availableLangs.map((value: string) => ({
        text: t(`lang_${value}`),
        value,
      })),
    [availableLangs, t],
  );

  const { firstName, lastName, profilePicture } = useMemo(
    () => user?.me ?? ({} as UserFragment),
    [user?.me],
  );

  // Handlers
  const handleToggleProfile = useCallback(() => push(ROUTES.PROFILE), [push]);

  const handleLangChange = useCallback(
    (value: string = "es") => setLanguage(value),
    [setLanguage],
  );

  // Notifications
  const { data } = useGetUnreadNotificationsCountQuery();
  const unreadNotificationsCount = useMemo(
    () => data?.unreadNotificationsCount || 0,
    [data],
  );

  const handleNotificationNavigation = useCallback(() => {
    push(ROUTES.NOTIFICATIONS);
  }, [push]);

  return (
    <>
      <IonHeader id="MainHeader">
        <div className="headerToolbar">
          <Link to={ROUTES.HOME}>
            <Image src="header" ext="svg" className="animatedLogo" />
          </Link>
          <div className="spacer" />
          <div
            className="notificationContainer"
            onClick={handleNotificationNavigation}
          >
            {unreadNotificationsCount > 0 && (
              <div className="notificationNumber">
                {unreadNotificationsCount > 99
                  ? "99+"
                  : unreadNotificationsCount}
              </div>
            )}
            <Icon icon="bell" />
          </div>
          <div className="langButton px-ms">
            <Button
              size="small"
              text={t(`lang_${currentLang}`)}
              dropdownValues={langValues}
              onClick={handleLangChange}
            />
          </div>
          {displayName && !!firstName && !!lastName && (
            <p>
              {firstName} {lastName}
            </p>
          )}
          <div className="deskTopUserPicture">
            <UserPicture
              firstName={firstName || "-"}
              lastName={lastName || "-"}
              userPicture={profilePicture}
              onClick={handleToggleProfile}
            />
          </div>
        </div>
      </IonHeader>
    </>
  );
};

export default MainHeader;
